import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ShieldCheck, Plus, Pencil, Trash2, LogOut, MonitorSmartphone, UserPlus, KeyRound, Ban, Copy, Mail, History } from 'lucide-react';
import { hasRole, type Role } from '@conversia/shared-types';
import * as rolesApi from '@/api/roles.api';
import type { Accion, EntradaDeActividad, PantallaDeRoles, RolDeNegocio, RolNuevo } from '@/api/roles.api';
import { ApiError } from '@/api/client';
import { Button } from '@/components/ui/Button';
import { Card, CardBody } from '@/components/ui/Card';
import { Badge } from '@/components/ui/Badge';
import { Avatar } from '@/components/ui/Avatar';
import { Spinner } from '@/components/ui/Spinner';
import { EmptyState } from '@/components/ui/EmptyState';
import { Modal } from '@/components/ui/Modal';
import { Input } from '@/components/ui/Input';
import { Select } from '@/components/ui/Select';
import { PageHeader } from '@/components/common/PageHeader';
import { roleLabel } from '@/lib/badges';
import { useAuthStore } from '@/store/auth.store';
import { useToast } from '@/hooks/useToast';

/**
 * ROLES Y PERMISOS (estándar Codeus): el negocio arma sus roles de departamento
 * y se los pone a su gente. Lo que se marca aquí lo cumple el SERVIDOR; esta
 * pantalla solo lo escribe.
 */
const NOMBRE_ACCION: Record<Accion, string> = { ver: 'Ver', crear: 'Crear', editar: 'Editar', borrar: 'Borrar', aprobar: 'Aprobar' };
const vacio: RolNuevo = { nombre: '', descripcion: '', permisos: {}, camposOcultos: [], topes: {} };
const mensaje = (err: unknown, otro: string) => (err instanceof ApiError ? err.message : otro);

function EditorDeRol({ abierto, rol, pantalla, onClose }: { abierto: boolean; rol: RolDeNegocio | null; pantalla: PantallaDeRoles; onClose: () => void }) {
  const qc = useQueryClient();
  const toast = useToast();
  const [d, setD] = useState<RolNuevo>(vacio);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!abierto) return;
    setD(rol ? { nombre: rol.nombre, descripcion: rol.descripcion ?? '', permisos: rol.permisos, camposOcultos: rol.camposOcultos, topes: rol.topes } : vacio);
    setError(null);
  }, [abierto, rol]);

  const guardar = useMutation({
    mutationFn: () => (rol ? rolesApi.editar(rol.id, d) : rolesApi.crear(d)),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['roles'] });
      toast.success(rol ? 'Rol guardado' : 'Rol creado');
      onClose();
    },
    onError: (err) => setError(mensaje(err, 'No se pudo guardar el rol')),
  });

  const conmutar = <T,>(lista: T[], valor: T) => (lista.includes(valor) ? lista.filter((v) => v !== valor) : [...lista, valor]);
  const alternarPermiso = (area: string, accion: Accion) =>
    setD((x) => ({ ...x, permisos: { ...x.permisos, [area]: conmutar(x.permisos[area] ?? [], accion) } }));
  const alternarCampo = (id: string) => setD((x) => ({ ...x, camposOcultos: conmutar(x.camposOcultos, id) }));
  // El chat en vivo lleva el contacto completo: sin teléfono o correo, los chats se ven al recargar.
  const sinVivo = (d.permisos.conversaciones?.includes('ver') ?? false) && d.camposOcultos.some((c) => c === 'telefono' || c === 'correo');

  function ponerTope(id: string, texto: string) {
    setD((x) => {
      const topes = { ...x.topes };
      if (texto.trim() === '') delete topes[id];
      else topes[id] = Math.max(0, Math.floor(Number(texto) || 0));
      return { ...x, topes };
    });
  }

  return (
    <Modal
      open={abierto}
      onClose={onClose}
      size="xl"
      title={rol ? `Editar rol «${rol.nombre}»` : 'Nuevo rol'}
      description="Marca lo que puede hacer en cada área. Lo que no marques, no lo puede hacer."
      footer={
        <>
          <Button variant="ghost" type="button" onClick={onClose}>Cancelar</Button>
          <Button onClick={() => (d.nombre.trim().length < 2 ? setError('Ponle un nombre al rol') : guardar.mutate())} loading={guardar.isPending}>Guardar</Button>
        </>
      }
    >
      <div className="space-y-5">
        <div className="grid gap-3 sm:grid-cols-2">
          <Input label="Nombre" placeholder="Ventas, Caja, Finanzas…" value={d.nombre} onChange={(e) => setD({ ...d, nombre: e.target.value })} />
          <Input label="Para qué es (opcional)" value={d.descripcion ?? ''} onChange={(e) => setD({ ...d, descripcion: e.target.value })} />
        </div>

        <div className="overflow-x-auto rounded-lg border border-surface-border dark:border-slate-800">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-xs text-slate-500 dark:bg-slate-900 dark:text-slate-400">
              <tr>
                <th className="px-3 py-2 text-left font-medium">Área</th>
                {pantalla.acciones.map((a) => <th key={a} className="px-2 py-2 font-medium">{NOMBRE_ACCION[a]}</th>)}
              </tr>
            </thead>
            <tbody className="divide-y divide-surface-border dark:divide-slate-800">
              {pantalla.areas.map((area) => (
                <tr key={area.id}>
                  <td className="px-3 py-2 text-slate-800 dark:text-slate-100">{area.nombre}</td>
                  {pantalla.acciones.map((a) => (
                    <td key={a} className="px-2 py-2 text-center">
                      <input
                        type="checkbox"
                        className="h-5 w-5 accent-brand-600"
                        aria-label={`${NOMBRE_ACCION[a]} en ${area.nombre}`}
                        checked={d.permisos[area.id]?.includes(a) ?? false}
                        onChange={() => alternarPermiso(area.id, a)}
                      />
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="space-y-2">
          <p className="text-sm font-medium text-slate-700 dark:text-slate-300">Lo que no ve, en ninguna de sus áreas</p>
          <div className="flex flex-wrap gap-x-5 gap-y-2">
            {pantalla.campos.map((c) => (
              <label key={c.id} className="inline-flex min-h-[44px] items-center gap-2 text-sm text-slate-700 dark:text-slate-300">
                <input type="checkbox" className="h-5 w-5 accent-brand-600" checked={d.camposOcultos.includes(c.id)} onChange={() => alternarCampo(c.id)} />
                {c.nombre}
              </label>
            ))}
          </div>
          <p className="text-xs text-slate-400">Con algo oculto, este rol tampoco descarga los Excel.</p>
          {sinVivo && <p className="text-xs text-amber-600 dark:text-amber-400">Con el teléfono o el correo oculto, sus chats no se actualizan en vivo: los ve al recargar.</p>}
        </div>

        {pantalla.topes.length > 0 && (
          <div className="space-y-2">
            <p className="text-sm font-medium text-slate-700 dark:text-slate-300">Topes sin aprobación</p>
            <p className="text-xs text-slate-400">Si se pasa, el sistema lo frena y lo tiene que hacer alguien con «Aprobar» en esa área. Vacío = sin tope.</p>
            <div className="grid gap-3 sm:grid-cols-2">
              {pantalla.topes.map((t) => (
                <Input key={t.id} label={t.nombre} type="number" min={0} inputMode="numeric" value={d.topes[t.id] ?? ''} onChange={(e) => ponerTope(t.id, e.target.value)} />
              ))}
            </div>
          </div>
        )}
        {error && <p className="text-sm text-danger">{error}</p>}
      </div>
    </Modal>
  );
}

/** Lo que dice la bitácora, en palabras del negocio. */
const QUE: Record<string, string> = {
  'sesion.entrada': 'entró',
  'sesion.fallida': 'intentó entrar y falló',
  'sesion.cerrada': 'cerró la sesión de otra persona',
  'acceso.negado': 'pidió algo que su rol no permite',
  'tope.pasado': 'pasó un tope',
  'rol.crear': 'creó un rol',
  'rol.editar': 'editó un rol',
  'rol.borrar': 'borró un rol',
  'rol.asignar': 'cambió el rol de una persona',
  'persona.baja': 'dio de baja a una persona',
  'persona.alta': 'reactivó a una persona',
  'persona.clave_restablecida': 'mandó el enlace para restablecer una clave',
  'invitacion.crear': 'invitó a una persona',
  'invitacion.revocar': 'revocó una invitación',
};

function detalle(e: EntradaDeActividad): string {
  const m = e.metadataJson ?? {};
  const texto = (k: string) => (typeof m[k] === 'string' ? (m[k] as string) : null);
  if (e.action === 'acceso.negado') return [e.entityId, texto('rol') && `rol ${texto('rol')}`].filter(Boolean).join(' · ');
  if (e.action === 'tope.pasado') return `límite ${String(m.limite ?? '')}${texto('motivo') ? ` · ${texto('motivo')}` : ` · pidió ${String(m.pedido ?? '')}`}`;
  return texto('email') ?? texto('enviadoA') ?? texto('nombre') ?? texto('motivo') ?? (e.ip ? `IP ${e.ip}` : '');
}

function InvitarPersona({ abierto, pantalla, miRol, onClose, copiar }: { abierto: boolean; pantalla: PantallaDeRoles; miRol: Role; onClose: () => void; copiar: (enlace: string) => void }) {
  const qc = useQueryClient();
  const porRol = pantalla.porRolDeDepartamento;
  // Lo que el servidor deja dar: quien entra por su rol, solo roles que caben en el suyo y un
  // rol de siempre no más alto que el suyo; el admin, todo.
  const asignables = pantalla.roles.filter((r) => pantalla.asignables.includes(r.id));
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<Role>('AGENT');
  const [rolId, setRolId] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [creada, setCreada] = useState<{ enlace: string | null; correoEnviado: boolean; email: string } | null>(null);

  useEffect(() => {
    if (!abierto) return;
    setEmail(''); setRole(hasRole(miRol, 'AGENT') ? 'AGENT' : miRol); setRolId(porRol ? (asignables[0]?.id ?? '') : ''); setError(null); setCreada(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- se reinicia solo al abrir
  }, [abierto]);

  const invitar = useMutation({
    mutationFn: () => rolesApi.invitar({ email: email.trim(), role, rolDeNegocioId: role === 'ADMIN' ? null : rolId || null }),
    onSuccess: (r) => { void qc.invalidateQueries({ queryKey: ['roles'] }); setCreada({ enlace: r.enlace, correoEnviado: r.correoEnviado, email: r.email }); },
    onError: (err) => setError(mensaje(err, 'No se pudo invitar')),
  });

  const roles = ([
    { value: 'AGENT', label: 'Agente' },
    { value: 'VIEWER', label: 'Lector' },
    { value: 'TECNICO', label: 'Técnico' },
    { value: 'ADMIN', label: 'Administrador (ve todo el negocio)' },
  ] as const).filter((o) => (porRol ? o.value !== 'ADMIN' && hasRole(miRol, o.value) : true));

  return (
    <Modal
      open={abierto}
      onClose={onClose}
      size="md"
      title={creada ? 'Invitación lista' : 'Invitar persona'}
      description={creada ? undefined : 'Entra a este negocio con el rol que elijas. Vale 7 días.'}
      footer={
        creada ? (
          <Button onClick={onClose}>Listo</Button>
        ) : (
          <>
            <Button variant="ghost" onClick={onClose}>Cancelar</Button>
            <Button onClick={() => (/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim()) ? invitar.mutate() : setError('Escribe un correo válido'))} loading={invitar.isPending}>Invitar</Button>
          </>
        )
      }
    >
      {creada ? (
        <div className="space-y-3">
          {creada.enlace ? (
            <>
              <p className="text-sm text-slate-600 dark:text-slate-300">
                {creada.correoEnviado ? `Le mandamos el enlace a ${creada.email}. También puedes pasárselo por WhatsApp:` : `Este sistema todavía no manda correos: pásale este enlace a ${creada.email} por WhatsApp.`}
              </p>
              <p className="break-all rounded-md bg-slate-100 px-3 py-2 font-mono text-xs text-slate-800 dark:bg-slate-800 dark:text-slate-100">{creada.enlace}</p>
              <Button variant="outline" size="sm" onClick={() => copiar(creada.enlace!)}><Copy className="mr-1 h-3.5 w-3.5" /> Copiar enlace</Button>
            </>
          ) : (
            <p className="text-sm text-slate-600 dark:text-slate-300">Le mandamos la invitación a {creada.email} por correo. Vale 7 días.</p>
          )}
        </div>
      ) : (
        <div className="space-y-4">
          <Input label="Correo" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="persona@negocio.com" />
          <Select label="Rol de siempre" value={role} options={[...roles]} onChange={(e) => setRole(e.target.value as Role)} />
          {role !== 'ADMIN' && (
            <Select
              label="Rol de departamento"
              value={rolId}
              options={[...(porRol ? [] : [{ value: '', label: 'Sin rol de departamento' }]), ...asignables.map((r) => ({ value: r.id, label: r.nombre }))]}
              onChange={(e) => setRolId(e.target.value)}
            />
          )}
          {porRol && asignables.length === 0 && <p className="text-xs text-slate-400">Solo puedes invitar con un rol que quepa dentro del tuyo, y todavía no hay ninguno. Créalo arriba.</p>}
          {error && <p className="text-sm text-danger">{error}</p>}
        </div>
      )}
    </Modal>
  );
}

export default function Roles() {
  const qc = useQueryClient();
  const toast = useToast();
  const meId = useAuthStore((s) => s.user?.id);
  const [editor, setEditor] = useState<{ rol: RolDeNegocio | null } | null>(null);
  const [aBorrar, setABorrar] = useState<RolDeNegocio | null>(null);
  const [invitarAbierto, setInvitarAbierto] = useState(false);
  const [aDarDeBaja, setADarDeBaja] = useState<{ id: string; nombre: string } | null>(null);
  const soyAdmin = useAuthStore((s) => s.user?.role === 'ADMIN' || s.user?.role === 'OWNER');
  const miRol = useAuthStore((s) => s.user?.role ?? 'VIEWER');

  const { data: pantalla, isLoading, error } = useQuery({ queryKey: ['roles'], queryFn: rolesApi.getPantalla });
  const { data: sesiones } = useQuery({ queryKey: ['roles', 'sesiones'], queryFn: rolesApi.getSesiones, enabled: Boolean(pantalla) });
  const { data: actividad } = useQuery({ queryKey: ['roles', 'actividad'], queryFn: rolesApi.getActividad, enabled: Boolean(pantalla) });

  const asignar = useMutation({
    mutationFn: (v: { userId: string; rolId: string | null }) => rolesApi.asignar(v.userId, v.rolId),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ['roles'] }); toast.success('Rol puesto'); },
    onError: (err) => toast.error(mensaje(err, 'No se pudo poner el rol')),
  });
  const borrar = useMutation({
    mutationFn: (id: string) => rolesApi.borrar(id),
    onSuccess: (r) => {
      void qc.invalidateQueries({ queryKey: ['roles'] });
      toast.info(r.personasSinRol ? `Rol borrado. ${r.personasSinRol} persona(s) vuelven a su rol de siempre.` : 'Rol borrado');
      setABorrar(null);
    },
    onError: (err) => toast.error(mensaje(err, 'No se pudo borrar el rol')),
  });
  const cerrar = useMutation({
    mutationFn: (id: string) => rolesApi.cerrarSesion(id),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ['roles', 'sesiones'] }); toast.info('Sesión cerrada'); },
    onError: (err) => toast.error(mensaje(err, 'No se pudo cerrar la sesión')),
  });
  const refrescar = () => void qc.invalidateQueries({ queryKey: ['roles'] });
  const baja = useMutation({
    mutationFn: (userId: string) => rolesApi.darDeBaja(userId),
    onSuccess: () => { refrescar(); toast.info('Dada de baja: ya no entra y sus sesiones se cerraron'); setADarDeBaja(null); },
    onError: (err) => toast.error(mensaje(err, 'No se pudo dar de baja')),
  });
  const alta = useMutation({
    mutationFn: (userId: string) => rolesApi.reactivar(userId),
    onSuccess: () => { refrescar(); toast.success('Reactivada'); },
    onError: (err) => toast.error(mensaje(err, 'No se pudo reactivar')),
  });
  const clave = useMutation({
    mutationFn: (userId: string) => rolesApi.restablecerClave(userId),
    onSuccess: (r) => { refrescar(); toast.success(`Le llegó ${r.enviadoA ? `a ${r.enviadoA} ` : 'a su correo '}el enlace para poner una clave nueva (vale 1 hora)`); },
    onError: (err) => toast.error(mensaje(err, 'No se pudo mandar el enlace')),
  });
  const revocar = useMutation({
    mutationFn: (id: string) => rolesApi.revocarInvitacion(id),
    onSuccess: () => { refrescar(); toast.info('Invitación revocada'); },
    onError: (err) => toast.error(mensaje(err, 'No se pudo revocar la invitación')),
  });
  const copiar = (enlace: string) =>
    void navigator.clipboard.writeText(enlace).then(() => toast.success('Enlace copiado: mándelo por WhatsApp'), () => toast.error(`No se pudo copiar; el enlace es: ${enlace}`));

  if (isLoading) return <div className="flex justify-center py-12"><Spinner /></div>;
  if (error || !pantalla) {
    return (
      <div className="p-6">
        <EmptyState icon={ShieldCheck} title="No tienes acceso a los roles" description={mensaje(error, 'Pídeselo a quien maneja los roles de tu negocio.')} />
      </div>
    );
  }

  // Quien entra por su rol solo ve en la lista lo que puede dar (y el rol que la persona ya tiene).
  const opcionesPara = (actual: string | null) => [
    ...(pantalla.porRolDeDepartamento ? [] : [{ value: '', label: 'Sin rol de departamento' }]),
    ...pantalla.roles
      .filter((r) => !pantalla.porRolDeDepartamento || pantalla.asignables.includes(r.id) || r.id === actual)
      .map((r) => ({ value: r.id, label: r.nombre })),
  ];
  const cuantos = (id: string) => pantalla.personas.filter((p) => p.rolDeNegocioId === id).length;
  const invitarSinCorreo = pantalla.porRolDeDepartamento && !pantalla.correoActivo;

  return (
    <div className="space-y-6 p-6">
      <PageHeader
        title="Roles y permisos"
        description="Arma los roles de tu negocio por departamento y decide qué puede ver y hacer cada persona."
        action={<Button onClick={() => setEditor({ rol: null })}><Plus className="h-4 w-4" /> Nuevo rol</Button>}
      />

      <Card>
        <CardBody className="p-0">
          {pantalla.roles.length === 0 ? (
            <EmptyState icon={ShieldCheck} title="Todavía no hay roles" description="Crea el primero (Ventas, Caja, Finanzas…) y pónselo a tu gente abajo." className="m-5" />
          ) : (
            <ul className="divide-y divide-surface-border dark:divide-slate-800">
              {pantalla.roles.map((r) => {
                const areas = pantalla.areas.filter((a) => r.permisos[a.id]?.length).map((a) => a.nombre);
                return (
                  <li key={r.id} className="flex flex-wrap items-center gap-3 px-5 py-3">
                    <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-brand-100 text-brand-700 dark:bg-brand-900/40 dark:text-brand-300"><ShieldCheck className="h-4 w-4" /></span>
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm font-medium text-slate-900 dark:text-slate-100">{r.nombre}</p>
                      <p className="truncate text-xs text-slate-400">{areas.length ? areas.join(' · ') : 'Sin permisos todavía'}</p>
                    </div>
                    <Badge variant="default">{cuantos(r.id)} persona(s)</Badge>
                    <Button variant="ghost" size="sm" title="Editar rol" onClick={() => setEditor({ rol: r })}><Pencil className="h-4 w-4" /></Button>
                    <Button variant="ghost" size="sm" title="Borrar rol" onClick={() => setABorrar(r)}><Trash2 className="h-4 w-4 text-danger" /></Button>
                  </li>
                );
              })}
            </ul>
          )}
        </CardBody>
      </Card>

      <Card>
        <CardBody className="p-0">
          <div className="flex flex-wrap items-center justify-between gap-3 border-b border-surface-border px-5 py-3 dark:border-slate-800">
            <h2 className="text-sm font-semibold text-slate-900 dark:text-slate-100">Personas del negocio</h2>
            <Button size="sm" disabled={invitarSinCorreo} title={invitarSinCorreo ? 'Sin correo configurado, invita un administrador' : undefined} onClick={() => setInvitarAbierto(true)}>
              <UserPlus className="h-4 w-4" /> Invitar persona
            </Button>
          </div>
          {!pantalla.correoActivo && (
            <p className="border-b border-surface-border bg-amber-50 px-5 py-2 text-xs text-amber-700 dark:border-slate-800 dark:bg-amber-900/20 dark:text-amber-300">
              {pantalla.porRolDeDepartamento
                ? 'Este sistema todavía no manda correos: las invitaciones las hace un administrador, y la clave temporal la da Silva Notion.'
                : 'Este sistema todavía no manda correos: las invitaciones se pasan copiando el enlace, y la clave temporal la da Silva Notion.'}
            </p>
          )}
          {pantalla.personas.length === 0 ? (
            <EmptyState icon={ShieldCheck} title="Sin personas" description="Invita a tu gente con el botón de arriba y ponle su rol." className="m-5" />
          ) : (
            <ul className="divide-y divide-surface-border dark:divide-slate-800">
              {pantalla.personas.map((p) => (
                <li key={p.id} className="flex flex-wrap items-center gap-3 px-5 py-3">
                  <Avatar name={p.name ?? p.email} size="md" />
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-medium text-slate-900 dark:text-slate-100">{p.name ?? '—'} {p.id === meId && <span className="text-xs text-slate-400">(tú)</span>}</p>
                    <p className="truncate text-xs text-slate-400">{p.email} · {roleLabel[p.role]}</p>
                  </div>
                  {!p.isActive && <Badge variant="danger">Inactivo</Badge>}
                  {p.role === 'ADMIN' ? (
                    <span className="text-xs text-slate-400">Administrador: ve todo su negocio</span>
                  ) : (
                    <div className="w-full sm:w-60 sm:shrink-0">
                      <Select
                        aria-label={`Rol de ${p.name ?? p.email}`}
                        value={p.rolDeNegocioId ?? ''}
                        options={opcionesPara(p.rolDeNegocioId)}
                        disabled={p.id === meId || asignar.isPending}
                        onChange={(e) => asignar.mutate({ userId: p.id, rolId: e.target.value || null })}
                      />
                    </div>
                  )}
                  {p.id !== meId && (p.role !== 'ADMIN' || soyAdmin) && (
                    <div className="flex items-center gap-1">
                      <Button
                        variant="ghost"
                        size="sm"
                        title={pantalla.correoActivo ? 'Mandarle el enlace para poner una clave nueva' : 'Sin correo configurado: la clave temporal la da Silva Notion'}
                        disabled={!pantalla.correoActivo || !p.isActive}
                        onClick={() => clave.mutate(p.id)}
                        loading={clave.isPending && clave.variables === p.id}
                      >
                        <KeyRound className="h-4 w-4" />
                      </Button>
                      {p.otrosNegocios ? (
                        <span className="text-xs text-slate-400" title="Trabaja también en otro negocio de la cuenta">Baja: la da el dueño</span>
                      ) : p.isActive ? (
                        <Button variant="ghost" size="sm" title="Dar de baja" onClick={() => setADarDeBaja({ id: p.id, nombre: p.name ?? p.email })}>
                          <Ban className="h-4 w-4 text-danger" />
                        </Button>
                      ) : (
                        <Button variant="outline" size="sm" onClick={() => alta.mutate(p.id)} loading={alta.isPending && alta.variables === p.id}>Reactivar</Button>
                      )}
                    </div>
                  )}
                </li>
              ))}
            </ul>
          )}
          {pantalla.invitaciones.length > 0 && (
            <>
              <div className="border-y border-surface-border px-5 py-2 text-xs font-medium text-slate-500 dark:border-slate-800">Invitaciones pendientes</div>
              <ul className="divide-y divide-surface-border dark:divide-slate-800">
                {pantalla.invitaciones.map((inv) => (
                  <li key={inv.id} className="flex flex-wrap items-center gap-3 px-5 py-3">
                    <Mail className="h-4 w-4 shrink-0 text-amber-600" />
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm text-slate-800 dark:text-slate-100">{inv.email}</p>
                      <p className="truncate text-xs text-slate-400">
                        {roleLabel[inv.role]}{inv.rolDeNegocioId ? ` · ${pantalla.roles.find((r) => r.id === inv.rolDeNegocioId)?.nombre ?? ''}` : ''} · vence {new Date(inv.expiresAt).toLocaleDateString('es-DO')}
                      </p>
                    </div>
                    {inv.enlace && <Button variant="outline" size="sm" onClick={() => copiar(inv.enlace!)}><Copy className="mr-1 h-3.5 w-3.5" /> Copiar enlace</Button>}
                    <Button variant="ghost" size="sm" title="Revocar invitación" onClick={() => revocar.mutate(inv.id)} loading={revocar.isPending && revocar.variables === inv.id}>
                      <Trash2 className="h-4 w-4 text-danger" />
                    </Button>
                  </li>
                ))}
              </ul>
            </>
          )}
        </CardBody>
      </Card>

      <Card>
        <CardBody className="p-0">
          <div className="border-b border-surface-border px-5 py-3 dark:border-slate-800">
            <h2 className="text-sm font-semibold text-slate-900 dark:text-slate-100">Sesiones abiertas</h2>
          </div>
          {!sesiones || sesiones.length === 0 ? (
            <p className="px-5 py-4 text-sm text-slate-400">Nadie más tiene una sesión abierta ahora.</p>
          ) : (
            <ul className="divide-y divide-surface-border dark:divide-slate-800">
              {sesiones.map((s) => (
                <li key={s.id} className="flex flex-wrap items-center gap-3 px-5 py-3">
                  <MonitorSmartphone className="h-4 w-4 shrink-0 text-slate-400" />
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm text-slate-800 dark:text-slate-100">{s.user.name ?? s.user.email}</p>
                    <p className="truncate text-xs text-slate-400">
                      Desde {new Date(s.createdAt).toLocaleString('es-DO')}{s.ip ? ` · ${s.ip}` : ''}{s.userAgent ? ` · ${s.userAgent.slice(0, 60)}` : ''}
                    </p>
                  </div>
                  <Button variant="outline" size="sm" disabled={s.user.id === meId} onClick={() => cerrar.mutate(s.id)} loading={cerrar.isPending && cerrar.variables === s.id}>
                    <LogOut className="mr-1 h-3.5 w-3.5" /> Cerrar sesión
                  </Button>
                </li>
              ))}
            </ul>
          )}
        </CardBody>
      </Card>

      <Card>
        <CardBody className="p-0">
          <div className="border-b border-surface-border px-5 py-3 dark:border-slate-800">
            <h2 className="text-sm font-semibold text-slate-900 dark:text-slate-100">Actividad del equipo</h2>
            <p className="text-xs text-slate-400">Entradas, intentos fallidos, accesos negados, topes, roles, altas y bajas. Lo último primero.</p>
          </div>
          {!actividad || actividad.length === 0 ? (
            <p className="px-5 py-4 text-sm text-slate-400">Todavía no hay movimientos.</p>
          ) : (
            <ul className="max-h-[420px] divide-y divide-surface-border overflow-y-auto dark:divide-slate-800">
              {actividad.map((e) => (
                <li key={e.id} className="flex flex-wrap items-baseline gap-x-3 gap-y-1 px-5 py-2.5 text-sm">
                  <History className="h-3.5 w-3.5 shrink-0 self-center text-slate-400" />
                  <span className="text-xs tabular-nums text-slate-400">{new Date(e.createdAt).toLocaleString('es-DO')}</span>
                  <span className="font-medium text-slate-800 dark:text-slate-100">{e.user?.name ?? e.user?.email ?? 'Sistema'}</span>
                  <span className={e.action === 'acceso.negado' || e.action === 'sesion.fallida' || e.action === 'tope.pasado' ? 'text-danger' : 'text-slate-600 dark:text-slate-300'}>
                    {QUE[e.action] ?? e.action}
                  </span>
                  <span className="min-w-0 truncate text-xs text-slate-400">{detalle(e)}</span>
                </li>
              ))}
            </ul>
          )}
        </CardBody>
      </Card>

      <EditorDeRol abierto={editor !== null} rol={editor?.rol ?? null} pantalla={pantalla} onClose={() => setEditor(null)} />
      <InvitarPersona abierto={invitarAbierto} pantalla={pantalla} miRol={miRol} onClose={() => setInvitarAbierto(false)} copiar={copiar} />

      <Modal
        open={aDarDeBaja !== null}
        onClose={() => setADarDeBaja(null)}
        size="sm"
        title={`¿Dar de baja a ${aDarDeBaja?.nombre ?? ''}?`}
        description="No podrá entrar y sus sesiones se cierran ya. Su historia queda en la bitácora y se puede reactivar."
        footer={
          <>
            <Button variant="ghost" onClick={() => setADarDeBaja(null)}>Cancelar</Button>
            <Button variant="danger" onClick={() => aDarDeBaja && baja.mutate(aDarDeBaja.id)} loading={baja.isPending}>Dar de baja</Button>
          </>
        }
      >
        <p className="text-sm text-slate-500">Queda anotado quién la dio de baja y cuándo.</p>
      </Modal>

      <Modal
        open={aBorrar !== null}
        onClose={() => setABorrar(null)}
        size="sm"
        title={`¿Borrar el rol «${aBorrar?.nombre ?? ''}»?`}
        description={aBorrar && cuantos(aBorrar.id) ? `${cuantos(aBorrar.id)} persona(s) lo tienen: vuelven a su rol de siempre.` : 'Nadie lo tiene puesto.'}
        footer={
          <>
            <Button variant="ghost" onClick={() => setABorrar(null)}>Cancelar</Button>
            <Button variant="danger" onClick={() => aBorrar && borrar.mutate(aBorrar.id)} loading={borrar.isPending}>Borrar</Button>
          </>
        }
      >
        <p className="text-sm text-slate-500">Queda anotado en la bitácora quién lo borró y cuándo.</p>
      </Modal>
    </div>
  );
}
